import { useState, useEffect, useMemo } from 'react';

const MOBILE_BREAKPOINT = 768;
const STORAGE_KEY = 'user-view-preference';

interface MobileDetection {
  isMobile: boolean;
  isTablet: boolean;
  isTouchDevice: boolean;
  screenWidth: number;
  shouldUseMobile: boolean;
  userPreference: 'mobile' | 'desktop' | 'auto';
  setUserPreference: (pref: 'mobile' | 'desktop' | 'auto') => void;
}

// Detect mobile via user agent (runs once)
function detectMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  
  // Common mobile user agent patterns
  const mobilePatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /Opera Mini/i,
    /IEMobile/i,
    /Mobile/i,
  ];
  
  return mobilePatterns.some(pattern => pattern.test(userAgent));
}

// Detect touch capability
function detectTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

// Get initial screen width safely
function getInitialScreenWidth(): number {
  if (typeof window === 'undefined') return 1024;
  return window.innerWidth;
}

// Get stored user preference
function getStoredPreference(): 'mobile' | 'desktop' | 'auto' {
  if (typeof localStorage === 'undefined') return 'auto';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'mobile' || stored === 'desktop') return stored;
  } catch {
    // localStorage not available
  }
  return 'auto';
}

export function useMobileRedirect(): MobileDetection {
  const [screenWidth, setScreenWidth] = useState(getInitialScreenWidth);
  const [userPreference, setUserPreferenceState] = useState<'mobile' | 'desktop' | 'auto'>(getStoredPreference);
  
  // Static detections (don't change)
  const isMobileUserAgent = useMemo(() => detectMobileUserAgent(), []);
  const isTouchDevice = useMemo(() => detectTouchDevice(), []);
  
  // Dynamic screen width detection
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Derived values
  const isMobileWidth = screenWidth < MOBILE_BREAKPOINT;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  
  // Determine if we should use mobile interface
  // Priority: user preference > screen width > user agent
  const shouldUseMobile = useMemo(() => {
    if (userPreference === 'mobile') return true;
    if (userPreference === 'desktop') return false;
    
    // Auto detection: screen width takes priority, then user agent
    return isMobileWidth || (isMobileUserAgent && screenWidth < 1024);
  }, [userPreference, isMobileWidth, isMobileUserAgent, screenWidth]);
  
  const setUserPreference = (pref: 'mobile' | 'desktop' | 'auto') => {
    setUserPreferenceState(pref);
    try {
      if (pref === 'auto') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, pref);
      }
    } catch {
      // localStorage not available
    }
  };
  
  return {
    isMobile: isMobileWidth || isMobileUserAgent,
    isTablet,
    isTouchDevice,
    screenWidth,
    shouldUseMobile,
    userPreference,
    setUserPreference,
  };
}

// Static function for immediate detection (use in useState initializers)
export function detectMobileImmediate(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Use the available viewport so an unfolded phone can use the expanded layout.
  return window.innerWidth < MOBILE_BREAKPOINT;
}
